import React, { useEffect, useRef, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { Link } from "react-router-dom";
import axios from "../../API/axios";
import {
  applyDeleteHubToChatList,
  markChatManuallyUnread,
  clearChatManuallyUnread,
} from "../../utils/chatReadUtils";
import { getDisplayMessageText } from "../../utils/messageReplyUtils";
import SuccessToast from "../SuccessToast";
import WarningToast from "../WarningToast";
import ErrorToast from "../ErrorToast";
import MoreHorizSharpIcon from '@mui/icons-material/MoreHorizSharp';
import ReplyOutlinedIcon from '@mui/icons-material/ReplyOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import ForwardOutlinedIcon from '@mui/icons-material/ForwardOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import DeleteOutlineSharpIcon from '@mui/icons-material/DeleteOutlineSharp';
import { useTranslation } from "react-i18next";

const MenuDivider = () => <div className="chat-message-menu-divider" role="separator" />;

const ChatMenuItem = ({ label, icon, onClick, danger = false }) => (
  <Dropdown.Item as="button" className={`dropdownList${danger ? " chat-message-menu-item-danger" : ""}`}>
    <Link
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
    >
      <div className="d-flex align-items-center w-100 chat-message-menu-item">
        <span className="chat-message-menu-icon">{icon}</span>
        <span className="dropdownTxt f-12">{label}</span>
      </div>
    </Link>
  </Dropdown.Item>
);

const ChatInteraction = ({ getChatList, chatId, item, setChatList, onReplyMessage, onForwardMessage }) => {
  const { t } = useTranslation();
  const sessionId = localStorage.getItem("sessionId");
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [menuPlacement, setMenuPlacement] = useState("bottom-start");
  const language = localStorage.getItem("Lang");

  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);

  const [warningToastMessage, setWarningToastMessage] = useState("");
  const [showWarningToast, setShowWarningToast] = useState(false);

  const [ErrorToastMessage, setErrorToastMessage] = useState("");
  const [showErrorToast, setShowErrorToast] = useState(false);

  const closeMenu = () => setShowDropdown(false);

  const resolveMenuPlacement = () => {
    if (!triggerRef.current) {
      return item?.isOwned ? "bottom-end" : "bottom-start";
    }

    const rect = triggerRef.current.getBoundingClientRect();
    const menuEstimate = 280;
    const footerReserve = 88;
    const headerReserve = 76;
    const spaceBelow = window.innerHeight - rect.bottom - footerReserve;
    const spaceAbove = rect.top - headerReserve;
    const openUpward = spaceBelow < menuEstimate && spaceAbove >= spaceBelow;

    if (openUpward) {
      return item?.isOwned ? "top-end" : "top-start";
    }

    return item?.isOwned ? "bottom-end" : "bottom-start";
  };

  const openMenu = () => {
    setMenuPlacement(resolveMenuPlacement());
    setShowDropdown(true);

    requestAnimationFrame(() => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      if (rect.bottom > window.innerHeight - 100) {
        triggerRef.current.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    });
  };

  const toggleMenu = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (showDropdown) {
      closeMenu();
    } else {
      openMenu();
    }
  };

  const canShowActions = item?.isDeleted === false && item?.isCompleteDeleted !== true;
  const hasCopyableText = Boolean(item?.msg?.trim());

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  const copyMessage = async () => {
    const text = getDisplayMessageText(item?.msg)?.trim();
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      setToastMessage(t("Notifications.Message copied"));
      setShowToast(true);
    } catch (e) {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "absolute";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setToastMessage(t("Notifications.Message copied"));
      setShowToast(true);
    }
  };

  const DeleteChat = async (deleteEveryOne) => {
    const responseData = {
      deviceType: "Web",
      sessionId: sessionId,
      chatID: chatId,
      forEveryOne: deleteEveryOne,
      langKey: language === "en" ? "" : language,
    };

    if (!deleteEveryOne) {
      localStorage.setItem("DeletedChatId", chatId);
    }

    const userUid = localStorage.getItem("userUid");
    const partnerUid = localStorage.getItem("toUserUid");

    if (deleteEveryOne && typeof setChatList === "function") {
      setChatList((prev) =>
        applyDeleteHubToChatList(
          prev,
          {
            chatID: chatId,
            senderUid: item?.isOwned ? userUid : item?.senderUid || partnerUid,
            receiverUid: item?.isOwned ? partnerUid : item?.receiverUid || userUid,
            isCompleteDeleted: true,
            sndrSideMsg: "You deleted this message",
            rcvrSideMsg: "This message was deleted",
          },
          userUid,
          partnerUid
        )
      );
    }

    try {
      await axios
        .delete(`Message/DeleteChat`, { data: responseData }).then((res) => {
          const hubPayload = res.data?.responseData || res.data || {};

          if (deleteEveryOne && typeof setChatList === "function") {
            setChatList((prev) =>
              applyDeleteHubToChatList(
                prev,
                {
                  chatID: chatId,
                  senderUid: item?.isOwned ? userUid : item?.senderUid || partnerUid,
                  receiverUid: item?.isOwned ? partnerUid : item?.receiverUid || userUid,
                  isCompleteDeleted: true,
                  sndrSideMsg: hubPayload.sndrSideMsg || "You deleted this message",
                  rcvrSideMsg: hubPayload.rcvrSideMsg || "This message was deleted",
                },
                userUid,
                partnerUid
              )
            );
          } else if (typeof setChatList === "function") {
            setChatList((prev) => {
              if (!Array.isArray(prev)) return prev;
              try {
                const updated = prev
                  .map((group) => {
                    if (!group || !Array.isArray(group.chats)) return group;
                    return {
                      ...group,
                      chats: group.chats.filter((c) => c.chatID !== chatId),
                    };
                  })
                  .filter((group) => Array.isArray(group.chats) ? group.chats.length > 0 : true);

                return updated;
              } catch (err) {
                return prev;
              }
            });
          }

          try {
            setToastMessage(res.data?.message || "Deleted successfully");
            setShowToast(true);
          } catch (err) {
            // ignore
          }

          if (!deleteEveryOne) {
            localStorage.setItem("DeletedChatId", chatId);
          }
        });
    } catch (e) {
      console.log(e);
    }
  };

  const ReadChat = async () => {
    const responseData = {
      deviceType: "Web",
      sessionId: sessionId,
      chatID: chatId,
      langKey: language === "en" ? "" : language,
    };
    try {
      clearChatManuallyUnread(localStorage.getItem("toUserUid"), chatId);
      await axios.post(`Message/ReadChat`, responseData).then(() => {
        if (typeof setChatList === "function") {
          setChatList((prev) => {
            if (!Array.isArray(prev)) return prev;
            return prev.map((group) => ({
              ...group,
              chats: (group.chats || []).map((chat) =>
                String(chat.chatID) === String(chatId)
                  ? { ...chat, isRead: true }
                  : chat
              ),
            }));
          });
        }
      });
    } catch (e) {
      console.log(e);
    }
  };

  const UnReadChat = async () => {
    const responseData = {
      deviceType: "Web",
      sessionId: sessionId,
      chatID: chatId,
      langKey: language === "en" ? "" : language,
    };
    try {
      markChatManuallyUnread(localStorage.getItem("toUserUid"), chatId);
      await axios.post(`Message/UnReadChat`, responseData).then(() => {
        if (typeof setChatList === "function") {
          setChatList((prev) => {
            if (!Array.isArray(prev)) return prev;
            return prev.map((group) => ({
              ...group,
              chats: (group.chats || []).map((chat) =>
                String(chat.chatID) === String(chatId)
                  ? { ...chat, isRead: false }
                  : chat
              ),
            }));
          });
        }
      });
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <>
      <div
        ref={triggerRef}
        className={`postMenu pointer chatInteraction${showDropdown ? " menu-open" : ""}`}
        onClick={toggleMenu}
      >
        <MoreHorizSharpIcon className="material-symbols-sharp" />

        {showDropdown && (
          <Dropdown.Menu
            show={showDropdown}
            ref={dropdownRef}
            placement={menuPlacement}
            className="InteractionModal chat-message-menu"
            popperConfig={{
              strategy: "fixed",
              placement: menuPlacement,
              modifiers: [
                {
                  name: "preventOverflow",
                  options: {
                    padding: { top: 76, bottom: 88, left: 8, right: 8 },
                    boundary: "viewport",
                    altAxis: true,
                  },
                },
                {
                  name: "flip",
                  options: {
                    fallbackPlacements: item?.isOwned
                      ? ["top-end", "top-start", "bottom-end", "bottom-start"]
                      : ["top-start", "top-end", "bottom-start", "bottom-end"],
                  },
                },
                {
                  name: "offset",
                  options: { offset: [0, 8] },
                },
              ],
            }}
          >
            {canShowActions && typeof onReplyMessage === "function" ? (
              <ChatMenuItem
                label={t("Notifications.Reply")}
                icon={<ReplyOutlinedIcon className="material-symbols-sharp" />}
                onClick={() => {
                  onReplyMessage(item);
                  closeMenu();
                }}
              />
            ) : null}

            {canShowActions && hasCopyableText ? (
              <ChatMenuItem
                label={t("Notifications.Copy")}
                icon={<ContentCopyOutlinedIcon className="material-symbols-sharp" />}
                onClick={() => {
                  copyMessage();
                  closeMenu();
                }}
              />
            ) : null}

            {canShowActions && hasCopyableText && typeof onForwardMessage === "function" ? (
              <ChatMenuItem
                label={t("Notifications.Forward")}
                icon={<ForwardOutlinedIcon className="material-symbols-sharp" />}
                onClick={() => {
                  onForwardMessage(item);
                  closeMenu();
                }}
              />
            ) : null}

            {canShowActions && item?.isOwned === false ? (
              item?.isRead === false ? (
                <ChatMenuItem
                  label={t("Notifications.Mark as Read")}
                  icon={<MarkEmailReadOutlinedIcon className="material-symbols-sharp" />}
                  onClick={() => {
                    ReadChat();
                    closeMenu();
                  }}
                />
              ) : (
                <ChatMenuItem
                  label={t("Notifications.Mark as UnRead")}
                  icon={<MarkEmailUnreadOutlinedIcon className="material-symbols-sharp" />}
                  onClick={() => {
                    UnReadChat();
                    closeMenu();
                  }}
                />
              )
            ) : null}

            {(item?.isDeleted === false || (item?.isOwned === true && item?.isCompleteDeleted === false)) ? (
              <MenuDivider />
            ) : null}

            {item?.isDeleted === false ? (
              <ChatMenuItem
                label={t("Notifications.Delete For Me")}
                icon={<DeleteOutlineSharpIcon className="material-symbols-sharp" />}
                danger
                onClick={() => {
                  DeleteChat(false);
                  closeMenu();
                }}
              />
            ) : null}

            {item?.isOwned === true && item?.isCompleteDeleted === false ? (
              <ChatMenuItem
                label={t("Notifications.Delete For Everyone")}
                icon={<DeleteOutlineSharpIcon className="material-symbols-sharp" />}
                danger
                onClick={() => {
                  DeleteChat(true);
                  closeMenu();
                }}
              />
            ) : null}
          </Dropdown.Menu>
        )}
      </div>

      <SuccessToast
        show={showToast}
        message={toastMessage}
        onClose={() => setShowToast(false)}
      />

      <WarningToast
        show={showWarningToast}
        message={warningToastMessage}
        onClose={() => setShowWarningToast(false)}
      />

      <ErrorToast
        show={showErrorToast}
        message={ErrorToastMessage}
        onClose={() => setShowErrorToast(false)}
      />
    </>
  );
};

export default ChatInteraction;
